import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LogOut, Package } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import api from "../lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const EMIRATES = ["Abu Dhabi", "Dubai", "Sharjah", "Ajman", "Umm Al Quwain", "Ras Al Khaimah", "Fujairah"];
const inputCls =
  "w-full bg-[#0D0D0D] border border-[#222] focus:border-[#E6DFD4] outline-none text-[#F5F5F5] px-4 py-3.5 text-sm placeholder:text-[#5a5a5a] transition-colors duration-300";

const StatusPill = ({ status }) => {
  const map = { paid: "bg-[#E6DFD4] text-black", pending: "bg-[#222] text-[#A3A3A3]", failed: "bg-red-900/40 text-red-300" };
  return <span className={`px-2.5 py-1 text-[10px] tracking-[0.15em] uppercase font-bold ${map[status] || map.pending}`}>{status}</span>;
};

export default function Account() {
  const { user, logout, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", line: "", city: "", emirate: "" });
  const [saving, setSaving] = useState(false);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "", phone: user.phone || "",
        line: user.address?.line || "", city: user.address?.city || "", emirate: user.address?.emirate || "",
      });
    }
  }, [user]);

  useEffect(() => {
    api.get("/orders/mine").then(({ data }) => setOrders(data)).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile({
        name: form.name, phone: form.phone,
        address: { line: form.line, city: form.city, emirate: form.emirate },
      });
      toast.success("Profile saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#050505] px-5 md:px-10 pt-28 pb-24">
      <div className="max-w-[1100px] mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#737373] mb-3">Account</div>
            <h1 className="font-display text-5xl md:text-6xl uppercase leading-none text-[#F5F5F5]">Hello, {user.name?.split(" ")[0] || "Athlete"}</h1>
            <p className="mt-2 text-sm text-[#A3A3A3]">{user.email}</p>
          </div>
          <div className="flex gap-3">
            {user.role === "admin" && (
              <button data-testid="go-admin-button" onClick={() => navigate("/admin")} className="px-5 py-3 border border-[#2c2c2c] text-[#E6DFD4] text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#111]">Admin</button>
            )}
            <button data-testid="logout-button" onClick={doLogout} className="flex items-center gap-2 px-5 py-3 border border-[#2c2c2c] text-[#A3A3A3] text-xs tracking-[0.2em] uppercase font-bold hover:text-[#E6DFD4]">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2">
            <h2 className="font-display text-2xl uppercase text-[#F5F5F5] mb-5">Delivery Details</h2>
            <form onSubmit={save} className="space-y-4">
              <input data-testid="profile-name-input" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
              <input data-testid="profile-phone-input" placeholder="Phone (e.g. 971501234567)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
              <input data-testid="profile-address-input" placeholder="Address (villa/apt, street, area)" value={form.line} onChange={(e) => setForm({ ...form, line: e.target.value })} className={inputCls} />
              <input data-testid="profile-city-input" placeholder="City / Area" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} />
              <Select value={form.emirate} onValueChange={(v) => setForm({ ...form, emirate: v })}>
                <SelectTrigger data-testid="profile-emirate-select" className={inputCls + " flex items-center justify-between"}>
                  <SelectValue placeholder="Select Emirate" />
                </SelectTrigger>
                <SelectContent className="bg-[#0D0D0D] border-[#222] text-[#F5F5F5]">
                  {EMIRATES.map((em) => (
                    <SelectItem key={em} value={em} data-testid={`emirate-option-${em.replace(/\s/g, "-")}`}>{em}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button data-testid="profile-save-button" type="submit" disabled={saving} className="w-full bg-[#E6DFD4] text-black py-4 text-xs tracking-[0.2em] uppercase font-bold hover:bg-white transition-colors duration-300 disabled:opacity-50">
                {saving ? "Saving..." : "Save Details"}
              </button>
            </form>
          </div>

          <div className="lg:col-span-3">
            <h2 className="font-display text-2xl uppercase text-[#F5F5F5] mb-5">Order History</h2>
            {orders.length === 0 ? (
              <div className="border border-[#1c1c1c] p-10 text-center text-[#A3A3A3]">
                <Package size={32} className="mx-auto mb-4 text-[#2c2c2c]" />
                No orders yet.
              </div>
            ) : (
              <div className="space-y-4" data-testid="order-history-list">
                {orders.map((o) => (
                  <div key={o.order_id} data-testid={`order-row-${o.order_id}`} className="border border-[#1c1c1c] bg-[#0A0A0A] p-5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs tracking-[0.15em] uppercase text-[#737373]">{o.order_id}</span>
                      <div className="flex items-center gap-2">
                        <StatusPill status={o.payment_status === "paid" ? "paid" : o.status} />
                        {o.payment_status === "paid" && o.fulfillment_status && o.fulfillment_status !== "pending" && (
                          <span data-testid={`fulfillment-badge-${o.order_id}`} className="px-2.5 py-1 text-[10px] tracking-[0.15em] uppercase font-bold border border-[#2c2c2c] text-[#E6DFD4]">
                            {o.fulfillment_status.replace(/_/g, " ")}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 text-sm text-[#C9C4BB]">
                      {o.items.map((i) => `${i.name} x${i.quantity}`).join(", ")}
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-xs text-[#737373]">{new Date(o.created_at).toLocaleDateString()}</span>
                      <span className="font-display text-2xl text-[#E6DFD4]">AED {o.total}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
